import { hex } from '@scure/base';
import { Address, OutScript, Script, Transaction } from '@scure/btc-signer';
import { TransactionInput, TransactionOutput } from '@scure/btc-signer/psbt';
import { isMatch } from 'lodash/fp';

import { BtcLikeNetwork } from '../types';

import {
  BtcCpfpTxData,
  getBtcCpfpChain,
  getLatestCpfpDescendant,
  getMempoolTxsSpendingUtxo,
  getTxData,
  getTxHex,
  getTxRbfTimeline,
  getTxStatus,
  getUtxos,
  RbfReplacementTimeline,
  type ResultUtxo,
} from './api';
import {
  INPUT_COUNT_BYTE,
  LOCK_TIME_BYTES,
  MARKER_FLAG_BYTES,
  OP_RETURN_LENGTH_BYTE,
  OUTPUT_AMOUNT_BYTES,
  OUTPUT_COUNT_BYTE,
  OUTPUT_LENGTH_BYTE,
  OUTPUT_MULTISIG_OPS_BYTES,
  OUTPUT_MULTISIG_PUBKEY_BYTES,
  OUTPUT_MULTISIG_PUBKEY_LENGTH_BYTES,
  OUTPUT_PKH_SCRIPT_PUBKEY_BYTES,
  OUTPUT_SH_SCRIPT_PUBKEY_BYTES,
  OUTPUT_WPKH_SCRIPT_PUBKEY_BYTES,
  P2PKH_INPUT_BYTE_SIZE_LEGACY,
  P2SH_P2WPKH_INPUT_BYTE_SIZE,
  P2SH_P2WPKH_INPUT_WITNESS_BYTE_SIZE,
  SIGOPS_INPUT_PKH_COUNT_LEGACY,
  SIGOPS_OUTPUT_BARE_MULTISIG_COUNT_LEGACY,
  SIGOPS_OUTPUT_PKH_COUNT_LEGACY,
  SIGOPS_WITNESS_SCALE_FACTOR,
  VERSION_BYTES,
} from './constants';
import { addInputUTXOsToTx, getBlockedWalletUtxosByInputs, getTransactionInputs, getTransactionOutputs } from './utils';

export const calculateInputByteSize = (input: TransactionInput) => {
  if (input.witnessUtxo || input.finalScriptWitness) {
    return {
      baseByteSize: P2SH_P2WPKH_INPUT_BYTE_SIZE,
      witnessByteSize: P2SH_P2WPKH_INPUT_WITNESS_BYTE_SIZE,
    };
  } else {
    return {
      baseByteSize: P2PKH_INPUT_BYTE_SIZE_LEGACY,
      witnessByteSize: 0,
    };
  }
};

export const calculateInputsByteSize = (tx: Transaction) => {
  const txInputs = getTransactionInputs(tx);

  const inputsByteSize = txInputs.reduce(
    (acc, cur) => {
      const { baseByteSize, witnessByteSize } = calculateInputByteSize(cur);
      return {
        baseByteSize: acc.baseByteSize + baseByteSize,
        witnessByteSize: acc.witnessByteSize + witnessByteSize,
      };
    },
    {
      baseByteSize: 0,
      witnessByteSize: 0,
    }
  );

  return inputsByteSize;
};

export const calculateOutputByteSize = (output: TransactionOutput) => {
  const { script } = output;

  let outputByteSize = OUTPUT_AMOUNT_BYTES + OUTPUT_LENGTH_BYTE;

  if (script) {
    const outputScript = OutScript.decode(script);
    const outputAsmScript = Script.decode(script);

    switch (outputScript.type) {
      case 'pkh':
        outputByteSize += OUTPUT_PKH_SCRIPT_PUBKEY_BYTES;
        break;
      case 'ms':
        // MofN MultiSig Output
        outputByteSize +=
          OUTPUT_MULTISIG_OPS_BYTES +
          outputScript.pubkeys.length * (OUTPUT_MULTISIG_PUBKEY_BYTES + OUTPUT_MULTISIG_PUBKEY_LENGTH_BYTES);
        break;
      case 'sh':
        // P-2-SH Output
        outputByteSize += OUTPUT_SH_SCRIPT_PUBKEY_BYTES;
        break;
      case 'wpkh':
        // P-2-WPKH Output
        outputByteSize += OUTPUT_WPKH_SCRIPT_PUBKEY_BYTES;
        break;
      case 'wsh':
        // P-2-WSH Output - Most likely this case will never happen, but it should be calculated correctly in the future.
        outputByteSize += OUTPUT_WPKH_SCRIPT_PUBKEY_BYTES;
        break;
      case 'unknown':
        // Potentially OP_RETURN or similar output, calculate byte size
        outputAsmScript.forEach((opValue) => {
          if (opValue instanceof Uint8Array) {
            // add up bytes
            outputByteSize += OP_RETURN_LENGTH_BYTE;
            outputByteSize += opValue.byteLength;
          } else {
            // if OP code, add 1 byte
            outputByteSize += 1;
          }
        });
        break;
    }
  }

  return outputByteSize;
};

export const calculateOutputSigOp = (output: TransactionOutput) => {
  const { script } = output;

  let outputSigOps = 0;

  if (script) {
    const outputScript = OutScript.decode(script);

    switch (outputScript.type) {
      case 'pkh':
        // (LEGACY)
        outputSigOps += SIGOPS_OUTPUT_PKH_COUNT_LEGACY; // 1 SigOps
        outputSigOps *= SIGOPS_WITNESS_SCALE_FACTOR; // x4 scale for being legacy
        break;
      case 'ms':
        // Bare multisig (MofN multisig in tx body = LEGACY)
        outputSigOps += SIGOPS_OUTPUT_BARE_MULTISIG_COUNT_LEGACY; // 20 SigOps per bare multisig
        outputSigOps *= SIGOPS_WITNESS_SCALE_FACTOR; // x4 scale for being legacy
        break;
      default:
        outputSigOps += 0;
    }
  }

  return outputSigOps;
};

export const calculateInputSigOp = (input: TransactionInput) => {
  const { redeemScript, witnessUtxo } = input;
  const script = redeemScript || witnessUtxo?.script;

  let inputSigOps = 0;

  if (script) {
    const inputScript = OutScript.decode(script);

    switch (inputScript.type) {
      case 'pkh':
        inputSigOps += SIGOPS_INPUT_PKH_COUNT_LEGACY;
        break;
    }
  }

  return inputSigOps;
};

export const calculateOutputsSigOps = (tx: Transaction) => {
  const txOutputs = getTransactionOutputs(tx);

  const outputsSigOps = txOutputs.reduce((acc, cur) => {
    return acc + calculateOutputSigOp(cur);
  }, 0);

  return outputsSigOps;
};

export const calculateInputsSigOps = (tx: Transaction) => {
  const txInputs = getTransactionInputs(tx);

  const inputsSigOps = txInputs.reduce((acc, cur) => {
    return acc + calculateInputSigOp(cur);
  }, 0);

  return inputsSigOps;
};

export const calculateOutputsByteSize = (tx: Transaction) => {
  const txOutputs = getTransactionOutputs(tx);

  const outputsByteSize = txOutputs.reduce((acc, cur) => {
    return acc + calculateOutputByteSize(cur);
  }, 0);

  return OUTPUT_COUNT_BYTE + outputsByteSize;
};

export const calculateBtcTransactionSize = (tx: Transaction) => {
  const inputsByteSize = calculateInputsByteSize(tx);
  const outputsByteSize = calculateOutputsByteSize(tx);

  const outputsSigOps = calculateOutputsSigOps(tx);
  const inputsSigOps = calculateInputsSigOps(tx);

  const txSigOps = outputsSigOps + inputsSigOps;

  const txVSizeByBytes =
    (VERSION_BYTES + INPUT_COUNT_BYTE + inputsByteSize.baseByteSize + outputsByteSize + LOCK_TIME_BYTES) * 1 +
    (MARKER_FLAG_BYTES + inputsByteSize.witnessByteSize) * 0.25;

  const txVSizeBySigops = txSigOps * 5;

  const txSize =
    VERSION_BYTES +
    MARKER_FLAG_BYTES +
    INPUT_COUNT_BYTE +
    inputsByteSize.baseByteSize +
    outputsByteSize +
    inputsByteSize.witnessByteSize +
    LOCK_TIME_BYTES;

  return {
    byteSize: txSize,
    sigOps: txSigOps,
    vSize: txVSizeByBytes,
    sizeBySigops: txVSizeBySigops,
    adjustedVSize: Math.ceil(txVSizeBySigops > txVSizeByBytes ? txVSizeBySigops : txVSizeByBytes),
  };
};

type UtxoCombinationFees = {
  isFeeCovered: boolean;
  txMinerFeeByVSize: bigint;
  mempoolTip: bigint;
  txTotalFee: bigint;
  change: bigint;
  uncoveredValue: bigint;
};

type UtxoCombinationFeesParams = {
  estimationTx: Transaction;
  utxosCombination: ResultUtxo[];
  satPerVbyteEstimate: number;
  walletAddress: string;
  publicKey: string;
  mempoolTip?: bigint;
  network: BtcLikeNetwork;
};

export const checkUtxoCombinationTxFee = async ({
  estimationTx,
  utxosCombination,
  satPerVbyteEstimate,
  walletAddress,
  publicKey,
  mempoolTip = 0n, // Additional expenses like CPFP, or just miner's tip
  network,
}: UtxoCombinationFeesParams): Promise<UtxoCombinationFees> => {
  let txClone = estimationTx.clone();

  txClone = (
    await addInputUTXOsToTx({
      tx: txClone,
      utxos: utxosCombination,
      walletAddress,
      publicKey,
      network,
    })
  ).txWithInputs;

  let txCloneWithChangeOutput = estimationTx.clone();

  txCloneWithChangeOutput.addOutput({
    script: OutScript.encode(Address(network.Settings).decode(walletAddress)),
    amount: 0n,
  });
  txCloneWithChangeOutput = (
    await addInputUTXOsToTx({
      tx: txCloneWithChangeOutput,
      utxos: utxosCombination,
      walletAddress,
      publicKey,
      network,
    })
  ).txWithInputs;

  // Check if change output makes sense in general
  let adjustedVSize = calculateBtcTransactionSize(txCloneWithChangeOutput).adjustedVSize;
  let txMinerFee = BigInt(Math.ceil(adjustedVSize * satPerVbyteEstimate));
  let availableChange = txCloneWithChangeOutput.fee - txMinerFee - mempoolTip;
  let uncoveredFee = txCloneWithChangeOutput.fee - (txMinerFee + mempoolTip);
  let totalTxFee = txCloneWithChangeOutput.fee;

  if (availableChange > 0n && availableChange < BigInt(network.DustLimit)) {
    // Merge the change into mempool tip if it does not make sense to have it as a separate output
    adjustedVSize = calculateBtcTransactionSize(txClone).adjustedVSize;
    txMinerFee = BigInt(Math.ceil(adjustedVSize * satPerVbyteEstimate));
    availableChange = txClone.fee - txMinerFee - mempoolTip;
    uncoveredFee = txClone.fee - (txMinerFee + mempoolTip);
    totalTxFee = txClone.fee;

    // pour unused change into mempool tip and set to 0n
    mempoolTip += availableChange;
    availableChange = 0n;
  }

  return {
    isFeeCovered: uncoveredFee >= 0n,
    txMinerFeeByVSize: txMinerFee,
    mempoolTip,
    txTotalFee: totalTxFee,
    change: availableChange,
    uncoveredValue: uncoveredFee >= 0n ? 0n : uncoveredFee,
  };
};

const checkUtxoCombinationTxFeeByUtxos =
  (curriedParams: Omit<UtxoCombinationFeesParams, 'utxosCombination'>) => (utxos: ResultUtxo[]) => {
    return checkUtxoCombinationTxFee({
      ...curriedParams,
      utxosCombination: utxos,
    });
  };

type UtxoCombination = {
  combination: ResultUtxo[];
  change: bigint;
  uncoveredValue: bigint;
} & UtxoCombinationFees;
/**
 * Select a fitting UTXO combination for a given transaction for a given wallet,
 * that covers all fees (inputs, outputs, and possible change)
 *
 * What is considered FITTING? A fitting combination is the one that:
 * - Covers all additional outputs
 * - Covers all transaction fees
 * - Uses the least amount of inputs (every input has a fee)
 * -- (Otherwise extended combinations may seem more fitting, but they potentially add 1..N additional transaction fees in inputs)
 */
export const getUtxoCombination = async ({
  tx,
  utxos,
  satPerVbyteEstimate,
  walletAddress,
  publicKey,
  existingTxId,
  network,
}: {
  tx: Transaction;
  utxos: ResultUtxo[];
  satPerVbyteEstimate: number;
  walletAddress: string;
  publicKey: string;
  existingTxId?: string;
  network: BtcLikeNetwork;
}): Promise<UtxoCombination> => {
  const calculateFeesByUtxos = async (utxoCombination: ResultUtxo[]) => {
    const txWithInputs = (
      await addInputUTXOsToTx({
        tx: tx.clone(),
        utxos: utxoCombination,
        walletAddress,
        publicKey,
        network,
      })
    ).txWithInputs;
    const { totalCpfpFee } = await checkCpfpFee({
      tx: txWithInputs,
      satPerVbyteEstimate,
      existingTxId,
      apiEndpoint: network.MempoolApiEndpoint,
    });

    // <0n means that (given the satPerByteEstimate) the CPFP txs already paid more
    // One cannot pay less than the parents' chain paid - in any direction it MUST be covered absolutely
    const absoluteCpfpFee = totalCpfpFee < 0n ? -totalCpfpFee : totalCpfpFee;

    return checkUtxoCombinationTxFeeByUtxos({
      estimationTx: tx,
      satPerVbyteEstimate,
      walletAddress,
      publicKey,
      mempoolTip: absoluteCpfpFee,
      network,
    })(utxoCombination);
  };

  let combination: ResultUtxo[] = [];

  const sortedUtxos = utxos.sort(({ value: a }, { value: b }) => b - a);

  // 1️. Collect UTXOs until fees are covered
  for (const utxo of sortedUtxos) {
    const { isFeeCovered } = await calculateFeesByUtxos(combination);
    if (isFeeCovered) break;
    combination.push(utxo); // Must come after checking coverage
  }

  const unusedUtxos = sortedUtxos
    .filter((utxo) => !combination.find(({ txid, vout }) => utxo.txid === txid && vout === utxo.vout))
    .sort(({ value: a }, { value: b }) => b - a);

  // 2. Try optimizing by swapping out larger UTXOs with smaller ones
  // Keep in mind already pre-added UTXOs in TX (e.g. RBF inputs are part of the initial TX payload)
  for (const utxo of unusedUtxos) {
    for (const [index] of combination.entries()) {
      const potentialCombination = combination.with(index, utxo);

      // if the smaller utxo fits, replace it
      if ((await calculateFeesByUtxos(potentialCombination)).isFeeCovered) {
        combination = potentialCombination;
        break;
      }
    }

    // if the utxo was not replaced, then all smaller UTXOs will not fit, break
    if (!combination.find(({ txid, vout }) => utxo.txid === txid && vout === utxo.vout)) {
      break;
    }
  }

  const txFees = await calculateFeesByUtxos(combination);

  return { ...txFees, combination };
};

/**
 * This function calculates how much fees have to be added to serve all unconfirmed parent txs
 * in the given CPFP chain.
 *
 * IMPORTANT: If existingTxId is provided, it must be the latest child of the CPFP chain.
 *
 * @param {Transaction} tx - The transaction to be CPFPed.
 * @param {number} satPerVbyteEstimate - The mempool fee sat/vbyte estimate.
 * @param {string} [existingTxId] - (Optional) The txid of the latest child that is being CPFPed (e.g. RBF).
 * @returns {Promise<bigint>} The calculated fee required for CPFP.
 */
export const checkCpfpFee = async ({
  tx,
  satPerVbyteEstimate,
  existingTxId,
  apiEndpoint,
}: {
  tx: Transaction;
  satPerVbyteEstimate: number;
  existingTxId?: string;
  apiEndpoint: string;
}): Promise<{ totalCpfpFee: bigint; totalVSize: number; currentCpfpTxFeesTotal: bigint }> => {
  if (existingTxId) {
    // In case of RBF make sure the existing tx is the latest child
    const latestChildTxId = await getLatestCpfpDescendant(existingTxId, apiEndpoint);

    if (latestChildTxId && latestChildTxId !== existingTxId) {
      throw new Error(`Transaction with txId ${existingTxId} has a CPFP descendant ${latestChildTxId}.`);
    }
  }

  const txInputIds = getTxIdsFromInputs(tx);
  const stack = new Set(txInputIds.concat(existingTxId || []));
  const memoizedFees = new Map<string, { txFeesTotal: number; txVSize: number; cpfpFee: number }>();

  while (stack.size > 0) {
    const txId = stack.values().next().value!;
    stack.delete(txId);

    const currentTx = await getTxData(txId, apiEndpoint);
    const { ancestors, descendants } = await getBtcCpfpChain(txId, apiEndpoint);

    const cpfpChainByTxId = [...(ancestors ?? []), currentTx, ...(descendants ?? [])];

    const allUnconfirmedTxs = (
      await Promise.all(
        cpfpChainByTxId.map(async (tx) => {
          if (memoizedFees.has(tx.txid)) return;
          if ((await getTxStatus(tx.txid, apiEndpoint)).confirmed) return;
          return tx;
        })
      )
    ).filter(
      (tx): tx is BtcCpfpTxData =>
        !!tx && // not undefined
        !memoizedFees.has(tx.txid) && // not already checked
        tx.txid !== existingTxId // not the current existing txId that is handled
    );

    for (const { txid, weight, fee: oldParentTxFee } of allUnconfirmedTxs) {
      const newAdditionalCpfpTxFee = (weight / 4) * satPerVbyteEstimate - oldParentTxFee;
      memoizedFees.set(txid, {
        txFeesTotal: oldParentTxFee,
        txVSize: weight / 4,
        cpfpFee: newAdditionalCpfpTxFee,
      });
      stack.add(txid);
    }
  }

  const totalCpfpFee = BigInt(Math.ceil([...memoizedFees.values()].reduce((acc, { cpfpFee }) => acc + cpfpFee, 0)));

  const totalVSize = [...memoizedFees.values()].reduce((acc, { txVSize }) => acc + txVSize, 0);

  const currentCpfpTxFeesTotal = BigInt(
    Math.floor([...memoizedFees.values()].reduce((acc, { txFeesTotal }) => acc + txFeesTotal, 0))
  );

  return { totalCpfpFee, totalVSize, currentCpfpTxFeesTotal };
};

// https://github.com/bitcoin/bips/blob/master/bip-0125.mediawiki#implementation-details
export const checkRbfMinTxRelayFee = async ({
  txId,
  newSatPerVbyteEstimateFee = 0,
  rbfTx,
  apiEndpoint,
}: {
  txId: string;
  newSatPerVbyteEstimateFee?: number;
  rbfTx?: Transaction;
  apiEndpoint: string;
}): Promise<{ isMinRelayTxFeeCovered: boolean; minRelayTxFeeSatPerVbyte: number; uncoveredMinRelayTxFee: number }> => {
  const rbfTxOrExisting =
    rbfTx ||
    Transaction.fromRaw(hex.decode(await getTxHex(txId, apiEndpoint)), {
      allowUnknownOutputs: true,
      disableScriptCheck: true,
      allowLegacyWitnessUtxo: true,
    });

  const [existingMempoolTx, cpfpChain, cpfpFee] = await Promise.all([
    getTxData(txId, apiEndpoint),
    getBtcCpfpChain(txId, apiEndpoint),
    checkCpfpFee({
      tx: rbfTxOrExisting,
      satPerVbyteEstimate: newSatPerVbyteEstimateFee,
      existingTxId: txId,
      apiEndpoint,
    }),
  ]);

  const existingTxFeeRate = existingMempoolTx.fee / (existingMempoolTx.weight / 4);
  const oldEffectiveSatPerVbyteRate = cpfpChain.effectiveFeePerVsize ?? existingTxFeeRate;
  const { totalCpfpFee } = cpfpFee;
  const feeRateDifference = newSatPerVbyteEstimateFee - oldEffectiveSatPerVbyteRate;
  const { adjustedVSize } = calculateBtcTransactionSize(rbfTxOrExisting);
  const computedFee = Math.ceil(feeRateDifference * adjustedVSize + Math.abs(Number(totalCpfpFee)));
  const minRelayTxFee = adjustedVSize;

  const uncoveredMinRelayTxFee = minRelayTxFee - computedFee < 0 ? 0 : minRelayTxFee - computedFee;

  const cpfpTotalParentsVSize = cpfpFee.totalVSize || 0;
  const minRelayTxFeeSatPerVbyte =
    Math.ceil((minRelayTxFee / (adjustedVSize + cpfpTotalParentsVSize) + oldEffectiveSatPerVbyteRate) * 100) / 100;

  return { isMinRelayTxFeeCovered: computedFee >= minRelayTxFee, minRelayTxFeeSatPerVbyte, uncoveredMinRelayTxFee };
};

export const getTxIdsFromInputs = (tx: Transaction): string[] => {
  return getTransactionInputs(tx)
    .filter((input): input is TransactionInput & { txid: Uint8Array } => Boolean(input.txid))
    .map((input) => hex.encode(input.txid));
};

const getUtxoCombinationWithUnconfirmed = async ({
  tx,
  utxos,
  walletAddress,
  publicKey,
  satPerVbyteEstimate,
  existingTxId,
  network,
}: {
  tx: Transaction;
  utxos: ResultUtxo[];
  walletAddress: string;
  publicKey: string;
  satPerVbyteEstimate: number;
  existingTxId?: string;
  network: BtcLikeNetwork;
}): Promise<UtxoCombination> => {
  // Cycle through two rounds: first with only confirmed UTXOs, then with all UTXOs.
  const onlyConfirmedUtxos = utxos.filter(({ status: { confirmed } }) => confirmed);
  const utxoGroups = [onlyConfirmedUtxos, utxos];

  let result: UtxoCombination | undefined;
  for (const utxos of utxoGroups) {
    result = await getUtxoCombination({
      tx,
      utxos,
      satPerVbyteEstimate,
      walletAddress,
      publicKey,
      existingTxId,
      network,
    });

    if (result.uncoveredValue >= 0n) break;
  }

  if (!result) {
    throw new Error('Something went wrong collecting UTXOs for the transaction.');
  }

  return result;
};

type FindFittingUtxosCombinationParams = {
  tx: Transaction;
  walletAddress: string;
  publicKey: string;
  vbyteEstimate?: number;
  includeUtxos?: ResultUtxo[];
  existingTxId?: string; // eg. if using RBF
  network: BtcLikeNetwork;
};

type FindFittingUtxosCombinationResult = {
  combination: ResultUtxo[];
  satPerVbyteEstimate: number;
} & UtxoCombinationFees;

export const findFittingBtcUtxoInputCombination = async (
  params: FindFittingUtxosCombinationParams
): Promise<FindFittingUtxosCombinationResult> => {
  const satPerVbyteEstimate =
    Number(params.vbyteEstimate) || (await params.network.api.getSatPerVbEstimateFee()).economyFee;

  const walletUtxos = await getUtxos(params.walletAddress, params.network.MempoolApiEndpoint);

  const manuallyIncludedUtxos = Array.isArray(params.includeUtxos) ? params.includeUtxos : [];

  const accessibleWalletUtxos = walletUtxos
    .filter((utxo) => params.existingTxId !== utxo.txid)
    .concat(manuallyIncludedUtxos);

  const blockedUtxos = getBlockedWalletUtxosByInputs(params.tx, accessibleWalletUtxos);

  const unblockedUtxos = accessibleWalletUtxos.filter(
    (utxo) => !blockedUtxos.find(({ l1TxId, vout }) => l1TxId === utxo.txid && vout === utxo.vout)
  );

  let unspentUtxos = (
    await Promise.all(
      unblockedUtxos.map(async (utxo) => {
        return (await getMempoolTxsSpendingUtxo(utxo.txid, utxo.vout, params.network.MempoolSpendingUtxoEndpoint))
          ?.length
          ? null
          : utxo;
      })
    )
  ).filter((i) => !!i);

  // If the transaction is a RBF, unconfirmed utxos MUST be excluded from the combination
  // except it was used as an input across the RBF timeline, which SHOULD be manually included
  // params.existingTxId signals RBF
  if (params.existingTxId) {
    unspentUtxos = unspentUtxos.filter(
      ({ txid, status }) => status.confirmed || manuallyIncludedUtxos.find(isMatch({ txid }))
    );
  }

  const { combination, ...txFees } = await getUtxoCombinationWithUnconfirmed({
    tx: params.tx,
    utxos: unspentUtxos,
    walletAddress: params.walletAddress,
    publicKey: params.publicKey,
    satPerVbyteEstimate,
    existingTxId: params.existingTxId,
    network: params.network,
  });

  return {
    combination,
    satPerVbyteEstimate,
    ...txFees,
  };
};

/**
 * Check if a transaction is a RBF transaction
 */
export const isRbfTimelineExists = async (txId: string, apiEndpoint: string): Promise<boolean> => {
  const txRbfTimeline = await getTxRbfTimeline(txId, apiEndpoint);

  return Boolean(txRbfTimeline?.replacements);
};

/**
 * Fetch and flatten the RBF timeline for a given transaction.
 * Returns a flat array of all unique transactions ids.
 */
export const getRbfReplacementTimelineTxIds = async (txId: string, apiEndpoint: string): Promise<string[]> => {
  const txRbfTimeline = await getTxRbfTimeline(txId, apiEndpoint);

  const gatherTxIds = (timeline: RbfReplacementTimeline): string[] => {
    return [timeline.tx.txid, ...(timeline.replaces?.flatMap(gatherTxIds) ?? [])];
  };

  return txRbfTimeline.replacements ? gatherTxIds(txRbfTimeline.replacements) : [];
};

/**
 * Fetches all inputs from all transactions in the RBF timeline.
 * Returns a flat array of all unique input UTXOs transactions ids.
 */
export const getRbfReplacementTimelineUtxos = async (txId: string, apiEndpoint: string): Promise<ResultUtxo[]> => {
  const txIds = await getRbfReplacementTimelineTxIds(txId, apiEndpoint);

  const allRbfTimelineUtxos: ResultUtxo[] = [];

  for (const id of txIds) {
    const txData = await getTxData(id, apiEndpoint);
    const txUtxos = txData.vin.map((input) => ({
      txid: input.txid,
      vout: input.vout,
      value: input.prevout.value,
      status: txData.status,
    }));
    allRbfTimelineUtxos.push(...txUtxos);
  }

  return allRbfTimelineUtxos;
};
